import {graphql} from 'react-apollo';
import {compose, withHandlers} from 'recompose';
import {COPY_PREVIOUS_WEEK_MUTATION} from '../../queries';
import {buildTsInput} from '../../../shared/utils/queries';
import makeBlankRow from './_makeBlankRow';

// Singleton queue key for a deferred "Copy Last Week" action.  Kept in sync
// with flushPendingSaves.js, which is the only other place that needs to
// recognise this queue item.
export const COPY_PREVIOUS_WEEK_QUEUE_KEY = 'copy-previous-week';

// copyPreviousWeek mutation
//
// When auto-save is ON (default) clicking Copy Last Week fires the mutation
// immediately after confirmation.  withAutoFlush (in timesheetActions.js)
// wraps copyPreviousWeekMutation so that any queued cell/row edits are
// committed first - otherwise the server-side copy would overwrite them.
//
// When auto-save is OFF the click instead queues a {kind: 'copyPreviousWeek'}
// item in pendingSaves.  The timesheet UI stays unchanged until the user
// clicks Save (or flips auto-save back ON) - at which point
// flushPendingSaves runs the queued copy first and then applies any other
// queued work.

export default compose(
  graphql(COPY_PREVIOUS_WEEK_MUTATION, {
    props: ({
      ownProps: {
        setError,
        setSuccess,
        clearLoading,
        setLoading,
        timesheet,
        updateTimesheetData
      },
      mutate
    }) => ({
      copyPreviousWeekMutation(includeTime) {
        setLoading();
        return mutate({
          variables: {
            ...buildTsInput(timesheet),
            includeTime
          },
          update(store) {
            // make sure we add a blank row.
            // Normally this is added when loading the timesheet, but here because the id is the same
            // it won't happen automatically
            updateTimesheetData(
              {
                rows: {
                  $push: [makeBlankRow(timesheet)]
                }
              },
              store
            );
          }
        })
          .then(
            ({data: {copyPreviousWeek: {message}}}) => {
              setSuccess(message.toString());
            },
            e => {
              setError(e);
            }
          )
          .then(clearLoading);
      }
    })
  }),
  withHandlers({
    // Queues a Copy Last Week action for the next batched save instead of
    // firing the mutation immediately.  Called from the confirmation dialog
    // success action when auto-save is OFF.  A copy replaces this week's
    // entries wholesale, so any cell/row edits already queued for this
    // timesheet are swept out first (mirrors the sweep in queueRowDelete).
    queueCopyPreviousWeek: ({
      queuePendingSave,
      clearPendingSave,
      pendingSaves,
      setSuccess
    }) => includeTime => {
      if (clearPendingSave && pendingSaves) {
        Object.keys(pendingSaves).forEach(key => {
          if (key === COPY_PREVIOUS_WEEK_QUEUE_KEY) return;
          clearPendingSave(key);
        });
      }

      queuePendingSave({
        kind: 'copyPreviousWeek',
        key: COPY_PREVIOUS_WEEK_QUEUE_KEY,
        includeTime
      });

      if (setSuccess) {
        setSuccess('Copy Last Week queued. Click Save to apply.');
      }
    }
  }),
  withHandlers({
    copyPreviousWeek: ({
      timesheet,
      setError,
      showConfirmation,
      autoSave
    }) => includeTime => {
      if (timesheet.Submitted) {
        setError('Timesheet has already been submitted');
        return;
      }

      const autoSaveOff = autoSave === false;
      const successAction = autoSaveOff
        ? ['queueCopyPreviousWeek', includeTime]
        : ['copyPreviousWeekMutation', includeTime];
      const message = autoSaveOff
        ? 'Auto-save is off. This will overwrite any queued changes for this week and apply when you click Save. Continue?'
        : "Are you sure you wish to copy the last week's timesheet?";

      showConfirmation({
        title: 'Copy Last Week',
        message,
        successAction
      });
    }
  }),
  withHandlers({
    copyPreviousWeek: ({copyPreviousWeek}) => e => {
      copyPreviousWeek(false);
    },
    copyPreviousWeekWithTime: ({copyPreviousWeek}) => e => {
      copyPreviousWeek(true);
    }
  })
);
